<script setup lang="ts">
import {
  EControlEventName,
  EGameOrientation,
  ERTCHookName,
  EWSEventName,
  EWSHookName,
  convertDeviceInfoToWsEndpoint,
  deviceControl,
  deviceWebRTC,
  deviceWebSocket,
  generateWsJoinDefaultParams,
  getWebsocketEndpoint,
  oldKeyConfig2NewKeyConfig,
} from '@/utils/cloud-device-sdk'
import { mobileAndTabletCheck } from '@/utils/cloud-device-sdk/utils'

import { sleep } from '@/utils'

import { API_GET_USER_BTN_CONFIG } from '@/api'
import { locales as WEBSITE_SUPPORT_LANG } from '@/contants/locales'
import KeyboardGui from '@/components/rtc-video/keyboard-gui.vue'
import { EToastType } from '@/types/components'

import type { IControlStateInfo, IPresetKeyboardMap } from '@/utils/cloud-device-sdk'

const props = defineProps<{
  deviceId: number
  cloudPhoneConnectPayload: {
    wssUrl: string
    phoneId: string
    accessPort: number
    publicIp: string
  }
  auth: {
    uid: string
    token: string
  }
  ipInfo: string | null
  gameInfo: {
    gameId: number
    packageName: string
    minutes: number
    screenOrientation: number
  }
  isReconnect: boolean
  buttonPosition: string | Record<string, never>
  muted: boolean
  quality: number
  isFullScreen: boolean
}>()

// emit
const emits = defineEmits(['updateRtt', 'videoPlaying', 'close'])

const { locale } = useI18n()
const { toast } = useToast()

// remote
const customKeyboardDataInject = inject<{
  customSetButtonId: string
  defaultConfigKeyboardPosition: IPresetKeyboardMap
  isInEditPresetKeyboardStatus: globalThis.Ref<boolean>
  currentStatusConfigKeyboardPosition: string
  updateSDKConfigKeyboardPosition: (newInfo: IControlStateInfo) => void
}>('custom-keyboard-data')

// show video display handle
let isPlaying = false
let deviceRotationCompleteResolve: (value: unknown) => void = () => {}
let deviceRotationComplete = new Promise((resolve) => {
  deviceRotationCompleteResolve = resolve
})

// render template ref
const isInScreenFollowMouseStatus = ref(false)
const isHorizontal = ref(false)
const videoElementRef = ref<HTMLVideoElement>()
const videoSection = ref<HTMLDivElement>()
const videoElementStyle = ref<Record<string, string>>()
const { width: videoSectionWidth, height: videoSectionHeight } = useElementSize(videoSection)
const { elementX: videoElementMouseX, elementY: videoElementMouseY, isOutside: mouseIsOutSideVideoSection } = useMouseInElement(videoSection)
const systemLang = computed(() => WEBSITE_SUPPORT_LANG.find(lang => lang.code === locale.value)?.androidLang || 'en-US')
const controlPanelRef = ref<HTMLDivElement>()
const addBtnPanelXY = ref<{ x: number, y: number } | Record<string, never>>({})
const { elementX: controlPanelMouseX, elementY: controlPanelMouseY, isOutside: mouseIsOutSideControlPanel } = useMouseInElement(controlPanelRef)

// logic variables
let deviceIPInfo: { ip: string, port: number } | null = null
const { connect, setParams, send, effect: wsEffect, close: closeWs } = deviceWebSocket()
const { init: initRtc, rtcEffect, createOffer, setRemoteDescription, close: closeRtc, dataChannelSend, videoUpdateTimeout, addCandidate } = deviceWebRTC(send, wsEffect)
const {
  down,
  move,
  up,
  setControlLayerElement,
  updateStatusInfo,
  destroy: destroyControl,
  effect: controlEffect,
  setControlArea,
  addControlArea,
  deleteControlArea,
  getStatusInfo,
  triggerSystemKey,
} = deviceControl(send, wsEffect, dataChannelSend)

// initialization provide function
if (customKeyboardDataInject)
  customKeyboardDataInject.updateSDKConfigKeyboardPosition = updateStatusInfo

// rtt update
rtcEffect(rtt => emits('updateRtt', rtt), [ERTCHookName.RTT])

// toggle device screen rotation
wsEffect<{ data: { rotation: number } }>(res => toggleRotateVideoElement(res.data.rotation), [EWSHookName.JOIN_RES, EWSHookName.NOTIFY])

// toggle mouse follow screen status
controlEffect<{ status: boolean }>((data) => {
  isInScreenFollowMouseStatus.value = data.status
}, [EControlEventName.TOGGLE_SCREEN_FOLLOW_SCREEN_STATUS])

async function init(url: string, phoneId: string, lang: string) {
  setParams({ url })
  const ws = await connect()
  ws?.addEventListener('close', () => emits('close'))
  ws?.addEventListener('error', () => {
    emits('close')
    try {
      window.__bl.error(new Error(`Device WSS Error: id:[${props.deviceId}], address:${ws.url}`))
    }
    catch (err) {}
  })

  const { uid, token } = props.auth
  // eslint-disable-next-line unused-imports/no-unused-vars
  const { screenOrientation, ...gameInfo } = props.gameInfo
  send(EWSEventName.JOIN, { ...generateWsJoinDefaultParams({
    deviceId: props.deviceId,
    uid,
    token,
    phoneId,
    gameInfo,
    ip: JSON.parse(props.ipInfo || '{"ip":""}').ip,
  }), sys_locale: lang })
}

wsEffect<{ data: { code: number } }>(async (res) => {
  if (res.data.code === 0) {
    let getForceCount = 0
    const IOSAutoForceInterval = setInterval(() => {
      if (getForceCount >= 5)
        clearInterval(IOSAutoForceInterval)

      send(EWSEventName.FORCEI, {})
      getForceCount += 1
    }, 2000)
    initRtc()
    const sdp = await createOffer()
    send(EWSEventName.OFFER, { sdp })
  }
  else {
    toast({ type: EToastType.ERROR, text: `JOIN ERROR!` })
    reset()
    try {
      window.__bl.error(new Error(`Connect Join Error: id:[${props.deviceId}], address:${deviceIPInfo?.ip}:${deviceIPInfo?.port}`))
    }
    catch (err) {}
  }
}, [EWSHookName.JOIN_RES])

wsEffect<{ data: { sdp: RTCSessionDescriptionInit } }>((res) => {
  if (deviceIPInfo && res.data.sdp)
    setRemoteDescription(res.data.sdp, { ip: deviceIPInfo.ip, port: deviceIPInfo.port + 2 })
}, [EWSHookName.ANSWER])

// add candidate
wsEffect<{ data: { candidate: string } }>((res) => {
  addCandidate(res.data.candidate)
}, [EWSEventName.CANDIDATE])

// update video stream
rtcEffect<MediaProvider>((stream) => {
  if (videoElementRef.value)
    videoElementRef.value.srcObject = stream
}, [ERTCHookName.STREAM])

rtcEffect(() => {
  reset()
  // todo: tips network error
  toast({ type: EToastType.ERROR, text: `NETWORK ERROR!` })

  try {
    window.__bl.error(new Error(`Connect Device Error: id:[${props.deviceId}], address:${deviceIPInfo?.ip}:${deviceIPInfo?.port}`))
  }
  catch (err) {}
}, [ERTCHookName.NETWORK_ERROR])

// start connect ws
watch(() => props.deviceId, async () => {
  // const { data } = await getWebsocketEndpoint(props.deviceId)
  // const endpoint = convertDeviceInfoToWsEndpoint(data.publicIp, data.accessPort)
  deviceIPInfo = {
    ip: props.cloudPhoneConnectPayload.publicIp,
    port: props.cloudPhoneConnectPayload.accessPort,
  }
  init(props.cloudPhoneConnectPayload.publicIp, props.cloudPhoneConnectPayload.phoneId, systemLang.value)
}, { immediate: true })

function resizeVideoElement() {
  if (props.isFullScreen && mobileAndTabletCheck()) {
    // mobile fullscreen reset default
    videoElementStyle.value = {}
    updateStatusInfo({ isHorizontal: false })
  }
  else {
    const offset = `${(videoSectionWidth.value - videoSectionHeight.value) / 2}px`
    videoElementStyle.value = { width: `${videoSectionHeight.value}px`, height: `${videoSectionWidth.value}px`, transform: `rotate(-90deg) translate(${offset}, ${offset})` }
  }
}

// resize video element
watch(() => videoSectionWidth.value, () => {
  isHorizontal.value && resizeVideoElement()
})

// preset control module rect details
watchOnce(() => controlPanelRef.value, () => {
  setControlLayerElement(controlPanelRef.value)
})

// notify control module horizontal change
watch(() => [isHorizontal.value], () => {
  if (props.isFullScreen && mobileAndTabletCheck() && isHorizontal.value)
    updateStatusInfo({ isHorizontal: false })
  else
    updateStatusInfo({ isHorizontal: isHorizontal.value })
})

// register preset keyboard data
watchOnce(() => [props.buttonPosition, props.auth.uid], async () => {
  const userCustomKey = await API_GET_USER_BTN_CONFIG({ uid: props.auth.uid, gameId: props.gameInfo.gameId, token: props.auth.token })
  let configKeyboard
  if (userCustomKey.length) {
    const { configData, buttonId } = userCustomKey[0]
    configKeyboard = JSON.parse(configData)
    if (customKeyboardDataInject)
      customKeyboardDataInject.customSetButtonId = buttonId
  }
  else {
    configKeyboard = oldKeyConfig2NewKeyConfig(props.buttonPosition, props.gameInfo.screenOrientation)
  }

  updateStatusInfo({ isHorizontal: isHorizontal.value, configKeyboard })
  if (customKeyboardDataInject) {
    customKeyboardDataInject.defaultConfigKeyboardPosition = configKeyboard
    customKeyboardDataInject.currentStatusConfigKeyboardPosition = configKeyboard
  }
}, { immediate: true })

// cloud phone rotation
function toggleRotateVideoElement(deg: number) {
  isHorizontal.value = deg > 0
  if (isHorizontal.value) {
    resizeVideoElement()
    deviceRotationCompleteResolve(true)
  }
  else {
    videoElementStyle.value = {}
  }

  /*
    backup logic
    if vertical screen game but not set true screenOrientation will hook
*/
  const timeoutTimer = setTimeout(() => {
    if (!isPlaying)
      deviceRotationCompleteResolve(true)
    clearTimeout(timeoutTimer)
  }, 5 * 1000)
}

async function videoHookPlaying() {
  if (!props.isReconnect) {
    // auto set device audio & quality
    send(EWSEventName.AUDIO_MUTE, { mute: props.muted })
    send(EWSEventName.SWITCH_QUALITY, { quality: props.quality })

    // skip screen rotation process
    const isLandscape = props.gameInfo.screenOrientation === EGameOrientation.LANDSCAPE
    isLandscape && (await deviceRotationComplete)
    // skip wakeup game app process
    await sleep(isLandscape ? 1500 : 2000)
  }
  emits('videoPlaying')
  isPlaying = true
}

function reset() {
  destroyControl()
  closeRtc()
  closeWs()
  isPlaying = false
  deviceRotationComplete = new Promise((resolve) => {
    deviceRotationCompleteResolve = resolve
  })
}

function tapMouseRightBtn(e: MouseEvent) {
  if (!isHorizontal.value && mouseIsOutSideControlPanel.value)
    return
  e.preventDefault()
  if (!customKeyboardDataInject?.isInEditPresetKeyboardStatus.value)
    return
  addBtnPanelXY.value = { x: isHorizontal.value ? videoElementMouseX.value : controlPanelMouseX.value, y: isHorizontal.value ? videoElementMouseY.value : controlPanelMouseY.value }
}

function saveButtonPositionToRemote() {
  const { configKeyboard } = getStatusInfo()
  return configKeyboard
}

// handle video muted & quality
watch(() => props.muted, () => send(EWSEventName.AUDIO_MUTE, { mute: props.muted }))
watch(() => props.quality, () => send(EWSEventName.SWITCH_QUALITY, { quality: props.quality }))

onBeforeUnmount(() => {
  reset()
})

defineExpose({
  reset,
  triggerSystemKey,
  saveButtonPositionToRemote,
})
</script>

<template>
  <section ref="videoSection" class="relative size-full select-none" :class="{ 'cursor-none': isInScreenFollowMouseStatus }" @contextmenu="tapMouseRightBtn">
    <div :style="videoElementStyle" class="relative size-full" :class="{ 'game-window': customKeyboardDataInject?.isInEditPresetKeyboardStatus.value }">
      <video
        ref="videoElementRef"
        class="size-full"
        webkit-playsinline="true"

        autoplay disablepictureinpicture playsinline
        x-webkit-airplay="allow"
        x5-video-player-type="h5"
        x5-video-player-fullscreen="true"
        x5-video-orientation="portraint"
        :muted="muted"
        @loadeddata="videoHookPlaying"
        @timeupdate="videoUpdateTimeout"
      />

      <!-- Hook control panel -->
      <section class="absolute left-0 top-0 size-full flex-center">
        <div ref="controlPanelRef" class="control-panel relative aspect-ratio-720/1280" :class="isHorizontal || (isFullScreen && mobileAndTabletCheck()) ? 'w-full' : 'h-full'" @mousedown="down" @mousemove="move" @mouseup="up" @touchstart="down" @touchmove="move" @touchend="up" @touchcancel="up">
          <KeyboardGui
            v-if="!isHorizontal"
            :control-effect="controlEffect"
            :get-status-info="getStatusInfo"
            :set-control-area="setControlArea"
            :add-control-area="addControlArea"
            :delete-control-area="deleteControlArea"
            :mouse-x="controlPanelMouseX"
            :mouse-y="controlPanelMouseY"
            :show-add-btn-panel="addBtnPanelXY"
            :mouse-in-game-area="!mouseIsOutSideControlPanel"
            :is-horizontal="isHorizontal"
            @hide-add-panel="addBtnPanelXY = {}"
            @freeze-control="(isFreezeControl: boolean) => updateStatusInfo({ isFreezeControl, isHorizontal })"
          />
        </div>
      </section>
    </div>
    <KeyboardGui
      v-if="isHorizontal"
      :control-effect="controlEffect"
      :get-status-info="getStatusInfo"
      :set-control-area="setControlArea"
      :add-control-area="addControlArea"
      :delete-control-area="deleteControlArea"
      :mouse-x="videoElementMouseX"
      :mouse-y="videoElementMouseY"
      :show-add-btn-panel="addBtnPanelXY"
      :mouse-in-game-area="!mouseIsOutSideVideoSection"
      :is-horizontal="isHorizontal"
      @hide-add-panel="addBtnPanelXY = {}"
      @freeze-control="(isFreezeControl: boolean) => updateStatusInfo({ isFreezeControl, isHorizontal })"
    />
  </section>
</template>

<style lang="scss" scoped>
.game-window {
  cursor: url(~/assets/svg/cursor.svg), default;
}
</style>
